import React from 'react';
import { formatEmiratesId } from '@/lib/formatters';
import type { StudentProfileV1 } from '@/types/studentprofile';
import StudentActions from '@/components/StudentActions';
import { resolveLatestEnrollment } from '@/lib/helpers';

interface StudentCardProps {
  student: StudentProfileV1;
  locale: string;
  t: any;
}

export function StudentCard({ student, locale, t }: StudentCardProps) {
  const displayName =
    locale === 'ar'
      ? [student.firstNameArabic, student.middleNameArabic, student.lastNameArabic].filter(Boolean).join(' ')
      : [
          student.firstNameEnglish,
          student.middleNameEnglish,
          student.thirdNameEnglish,
          student.fourthNameEnglish,
          student.familyNameEnglish,
        ]
          .filter(Boolean)
          .join(' ');

  const latestEnrollment = resolveLatestEnrollment(student.enrollment);
  const educationType = latestEnrollment?.educationType ?? null;
  const isPrivateEducation = educationType?.toLowerCase() === 'private';
  const studentNum = student.studentNumber || (student.id ? student.id.slice(-6) : '');

  return (
    <div className="group relative rounded-xl border bg-card p-4 sm:p-5 hover:border-primary/40 hover:shadow-sm transition-all duration-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left Info: Avatar + Details */}
        <div className="flex items-start sm:items-center gap-3.5 min-w-0">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold text-lg flex items-center justify-center flex-shrink-0">
            {(displayName || 'S').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors mb-1 truncate">
              {displayName || studentNum || student.id}
            </h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
              {studentNum && (
                <span className="flex items-center gap-1">
                  <span>{t.studentNumber}:</span>
                  <span className="font-mono font-medium">{studentNum}</span>
                </span>
              )}
              {student.emirateId && (
                <span className="flex items-center gap-1">
                  <span>{t.eid}:</span>
                  <span className="font-mono font-medium">{formatEmiratesId(student.emirateId)}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Info: Status Badge + Action */}
        <div className="flex items-center justify-between sm:justify-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
          {isPrivateEducation ? (
            <div className="aegov-badge font-medium text-xs border bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800">
              {locale === 'ar' ? 'خاص' : 'Private'}
            </div>
          ) : (
            student.isActive !== undefined && (
              <div
                className={`aegov-badge font-medium text-xs border ${
                  student.isActive
                    ? 'badge-success bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800'
                    : 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                }`}
              >
                {student.isActive ? (locale === 'ar' ? 'نشط' : 'Active') : locale === 'ar' ? 'غير نشط' : 'Inactive'}
              </div>
            )
          )}

          <StudentActions
            studentId={student.id}
            studentNumber={student.studentNumber}
            isActive={student.isActive && !isPrivateEducation}
          />
        </div>
      </div>
    </div>
  );
}
